import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import SheetIcon from "./SheetIcon";

export interface Opt {
    id: string;
    /** searchable text (MUI's default substring filter runs over this) */
    label: string;
    primary: string;
    secondary?: string;
    cm?: string | null;
    /** group header key (e.g. "Javaslatok" / "Katalógus"); enables grouping when any option has one */
    group?: string;
}

interface ComboProps {
    options: Opt[];
    placeholder: string;
    grouped?: boolean;
    autoOpen?: boolean;
    onPick: (id: string) => void;
}

/**
 * The same GUI solution as the configurator (MUI Autocomplete): inline control, popup listbox,
 * empty filter shows ALL options, client-side substring filter, suggested items grouped on top.
 */
const Combo: React.FC<ComboProps> = ({ options, placeholder, grouped, autoOpen, onPick }) => (
    <Autocomplete<Opt, false, false, false>
        key={grouped ? "grouped" : "flat"}
        options={options}
        size="small"
        fullWidth
        openOnFocus
        selectOnFocus
        handleHomeEndKeys
        disabled={options.length === 0}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        onChange={(_e, opt) => { if (opt) onPick(opt.id); }}
        {...(grouped ? { groupBy: (o: Opt) => o.group || "" } : {})}
        renderGroup={grouped ? (params) => (
            <Box key={params.key}>
                <Box sx={{ px: 1.5, py: 0.5, fontSize: 11, fontWeight: 700, letterSpacing: ".4px", color: "#8a8780", textTransform: "uppercase" }}>
                    {params.group}
                </Box>
                {params.children}
                <Divider sx={{ my: 0.5 }} />
            </Box>
        ) : undefined}
        renderInput={(params) => <TextField {...params} autoFocus={autoOpen} placeholder={placeholder} />}
        renderOption={(props, option) => (
            <li {...props} key={option.id} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {option.cm ? <SheetIcon cm={option.cm} /> : null}
                <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <span style={{ fontSize: 13 }}>{option.primary}</span>
                    {option.secondary ? <span style={{ fontSize: 11, color: "#8a8780" }}>{option.secondary}</span> : null}
                </span>
            </li>
        )}
    />
);

export default Combo;
